import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs";
import { ArrowLeft, Calendar } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useRunDetail } from "../../hooks";
import { BillsOverviewCard } from "./BillsOverviewCard";
import { CycleInfoCard } from "./CycleInfoCard";
import { QuickActionsCard } from "./QuickActionCard";
import { ActivityLogTab } from "./Tabs/ActivityLog";
import { BillsTab } from "./Tabs/BillsTab";
import { DetailsTab } from "./Tabs/DetailsTab";
import { ExcludedConsumersTab } from "./Tabs/ExcludedConsumers/ExcludedConsumers";

export const BulkBillingDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: batchDetails } = useRunDetail(id!);

  const handleBack = () => {
    navigate("/bx/create-bill-generation");
  };

  // Define tab components
  const tabComponents = {
    details: {
      label: "Details",
      component: <DetailsTab batchId={id!} />,
    },
    bills: {
      label: "Bills",
      component: <BillsTab batchId={id!} />,
    },
    "excluded-consumers": {
      label: "Excluded Consumers",
      component: <ExcludedConsumersTab />,
    },
    "activity-log": {
      label: "Activity Log",
      component: <ActivityLogTab batchId={id!} />,
    },
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-foreground">
                  {batchDetails?.cycleName}
                </h1>
                <span className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                  {batchDetails?.status}
                </span>
              </div>
            </div>
          </div>

          {/* Metadata */}

          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Created: {batchDetails?.createdDate}</span>
            </div>

            {/* <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>Execution: {batchDetails?.executionType}</span>
            </div> */}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Left Column - Cycle Info */}
          <div className="lg:col-span-2 space-y-6">
            <CycleInfoCard data={batchDetails} />
          </div>

          {/* Right Column - Overview and Actions */}
          <div className="space-y-6">
            <BillsOverviewCard data={batchDetails?.billsOverview} />
            <QuickActionsCard runId={id!} />
          </div>
        </div>

        {/* Tabs Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <Tabs
            tabComponents={tabComponents}
            defaultValue="details"
            tabsListClassName="grid w-full grid-cols-4 mb-6"
            urlMapping={{
              details: "details",
              bills: "bills",
              "excluded-consumers": "excluded",
              "activity-log": "activity",
            }}
            tabsListFullWidth={false}
            idPrefix="batch-detail-tab"
            level={0}
          />
        </div>
      </div>
    </div>
  );
};
